import React, { useEffect, useMemo, useState } from 'react';
import { getAliasFromNomeTema } from '../LayerAliasUtils';

/**
 * Configurador moderno de estilos usando Tailwind CSS
 * Exibe cada camada em uma aba e controla inputs de estilo
 */
const TailwindLayerStyleConfig = ({ layerNames, styles, onChange }) => {
  const safeLayerNames = useMemo(
    () => (Array.isArray(layerNames) ? layerNames.filter(Boolean) : []),
    [layerNames],
  );
  const [activeLayer, setActiveLayer] = useState(safeLayerNames[0] || '');

  useEffect(() => {
    if (!safeLayerNames.length) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setActiveLayer('');
      return;
    }
    if (!safeLayerNames.includes(activeLayer)) {
      setActiveLayer(safeLayerNames[0]);
    }
  }, [safeLayerNames, activeLayer]);

  const activeStyle = styles && activeLayer && styles[activeLayer] ? styles[activeLayer] : {};
  const aliasify = (n) =>
    n
      .split(' ')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ');

  return (
    <div className="p-4 space-y-4">
      {/* Tabs */}
      <div className="flex flex-wrap gap-2">
        {safeLayerNames.map((layer) => (
          <button
            key={layer}
            onClick={() => setActiveLayer(layer)}
            className={`px-3 py-1 text-sm rounded-full whitespace-nowrap transition-colors
              ${activeLayer === layer ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
          >
            {aliasify(getAliasFromNomeTema(layer))}
          </button>
        ))}
      </div>
      {/* Controls for active layer */}
      {!activeLayer ? (
        <div className="text-sm text-gray-600">Nenhuma camada disponível para configurar.</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-3">
          {/* Cor da Linha */}
          <div className="flex flex-col">
            <label className="text-xs font-medium mb-1">Cor da Linha</label>
            <div className="flex items-center space-x-2">
              <input
                type="color"
                value={activeStyle.strokeColor || '#000000'}
                onChange={(e) =>
                  onChange(activeLayer, { ...activeStyle, strokeColor: e.target.value })
                }
                className="w-6 h-6 p-0 rounded"
              />
              <span className="text-xs font-mono">{activeStyle.strokeColor || '#000000'}</span>
            </div>
          </div>
          {/* Preenchimento */}
          <div className="flex flex-col">
            <label className="text-xs font-medium mb-1">Preenchimento</label>
            <div className="flex items-center space-x-2">
              <input
                type="color"
                value={activeStyle.fillColor || '#000000'}
                onChange={(e) =>
                  onChange(activeLayer, { ...activeStyle, fillColor: e.target.value })
                }
                className="w-6 h-6 p-0 rounded"
              />
              <span className="text-xs font-mono">{activeStyle.fillColor || '#000000'}</span>
            </div>
          </div>
          {/* Estilo */}
          <div className="flex flex-col">
            <label className="text-xs font-medium mb-1">Estilo</label>
            <select
              className="text-xs p-1 border rounded"
              value={activeStyle.strokeDash || 'solid'}
              onChange={(e) =>
                onChange(activeLayer, { ...activeStyle, strokeDash: e.target.value })
              }
            >
              <option value="solid">Contínua</option>
              <option value="dash">Tracejada</option>
              <option value="dot">Pontilhada</option>
            </select>
          </div>
          {/* Espessura */}
          <div className="flex flex-col">
            <label className="text-xs font-medium mb-1">Espessura</label>
            <input
              type="number"
              min={1}
              max={10}
              value={activeStyle.strokeWeight ?? 1}
              onChange={(e) =>
                onChange(activeLayer, { ...activeStyle, strokeWeight: Number(e.target.value) })
              }
              className="text-xs p-1 border rounded w-full"
            />
          </div>
          {/* Opacidade Linha */}
          <div className="flex flex-col">
            <label className="text-xs font-medium mb-1">Opacidade Linha (%)</label>
            <div className="flex items-center space-x-2">
              <input
                type="number"
                min={0}
                max={100}
                value={Math.round((activeStyle.strokeOpacity ?? 0) * 100)}
                onChange={(e) =>
                  onChange(activeLayer, { ...activeStyle, strokeOpacity: e.target.value / 100 })
                }
                className="text-xs p-1 border rounded w-full"
              />
              <span className="text-xs">%</span>
            </div>
          </div>
          {/* Opacidade Preench. */}
          <div className="flex flex-col">
            <label className="text-xs font-medium mb-1">Opacidade Preenchimento (%)</label>
            <div className="flex items-center space-x-2">
              <input
                type="number"
                min={0}
                max={100}
                value={Math.round((activeStyle.fillOpacity ?? 0) * 100)}
                onChange={(e) =>
                  onChange(activeLayer, { ...activeStyle, fillOpacity: e.target.value / 100 })
                }
                className="text-xs p-1 border rounded w-full"
              />
              <span className="text-xs">%</span>
            </div>
          </div>
          {/* Raio */}
          <div className="flex flex-col">
            <label className="text-xs font-medium mb-1">Raio</label>
            <input
              type="number"
              min={1}
              max={20}
              value={activeStyle.radius ?? 5}
              onChange={(e) =>
                onChange(activeLayer, { ...activeStyle, radius: Number(e.target.value) })
              }
              className="text-xs p-1 border rounded w-full"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default TailwindLayerStyleConfig;
